/* eslint-disable no-unused-vars */
import { useContext, useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  auth,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
} from "../utils/firebase";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../utils/firebase";
import { rtdb } from "../utils/firebase";
import { useAuth } from "./AuthContext";
import { loadStripe } from "@stripe/stripe-js";
import { TokenContext } from "./tokenContext";
import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
} from "firebase/auth";
import { ref, set, onDisconnect, serverTimestamp } from "firebase/database";
import "./JobBankLogin.css";
import googleLogo from "./GoogleG.png";

function resolveMfaFactor(resv) {
  if (!resv) return null;
  const hints = resv.hints || [];
  if (hints.some((h) => h.factorId === "totp")) return "totp";
  if (hints.some((h) => !!h.phoneNumber)) return "sms";
  return "other";
}

function JobBankLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [unverifiedUser, setUnverifiedUser] = useState(null);
  const [smsCode, setSmsCode] = useState("");
  const [resolver, setResolver] = useState(null);
  const [totpCodeLogin, setTotpCodeLogin] = useState("");
  const [loginVerificationId, setLoginVerificationId] = useState(null);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loginPending, setLoginPending] = useState(false);
  const [googlePending, setGooglePending] = useState(false);
  const [verifyingMfa, setVerifyingMfa] = useState(false);
  const mfaInputRef = useRef(null);
  const loginLock = useRef(false);
  const googleLock = useRef(false);
  const verifyLock = useRef(false);

  const navigate = useNavigate();
  const { setUserRole } = useAuth();
  const { purchaseIntent, setPurchaseIntent, planIntent, setPlanIntent } =
    useContext(TokenContext);

  const mfaFactor = resolveMfaFactor(resolver);
  const mfaFieldReady =
    mfaFactor === "totp" ||
    mfaFactor === "other" ||
    (mfaFactor === "sms" && !!loginVerificationId);
  const showLoginChoices = !mfaFactor && !googlePending;

  useEffect(() => {
    if (mfaFieldReady && !verifyingMfa) {
      mfaInputRef.current?.focus();
    }
  }, [mfaFieldReady, verifyingMfa, error]);

  function initRecaptchaLogin() {
    if (!window.recaptchaVerifierLogin) {
      window.recaptchaVerifierLogin = new RecaptchaVerifier(
        auth,
        "recaptcha-login-container",
        { size: "invisible" }
      );
    }
  }

  async function ensurePlanState(uidOrRef) {
    try {
      const userRef =
        typeof uidOrRef === "string" ? doc(db, "users", uidOrRef) : uidOrRef;

      const snap = await getDoc(userRef);
      const u = snap.data() || {};
      const exp = u?.planInfo?.expiresAt?.toDate?.();

      if (u?.planInfo?.status === "active" && exp && exp <= new Date()) {
        await updateDoc(userRef, {
          userType: "token",
          "planInfo.status": "expired",
        });
      }
    } catch (e) {
      console.warn("ensurePlanState() skipped:", e);
    }
  }

  async function checkoutForPlanIntent(user, planKey) {
    try {
      const stripe = await loadStripe(process.env.REACT_APP_STP_TST);
      const idToken = await user.getIdToken();

      const res = await fetch(
        "https://fluency.ispeakwell.ca/api/create-plan-checkout-session",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            planKey,
            frontendOrigin: window.location.origin,
          }),
        }
      );

      if (!res.ok) {
        throw new Error(`create-plan-checkout-session failed: ${res.status}`);
      }

      const { sessionId } = await res.json();
      await stripe.redirectToCheckout({ sessionId });
    } finally {
      setPlanIntent(null);
    }
  }

  async function startMfaFlow(resv) {
    try {
      initRecaptchaLogin();

      const hints = resv.hints || [];
      const hasTotp = hints.some((h) => h.factorId === "totp");

      if (hasTotp) {
        setError("");
        setResolver(resv);
        return;
      }

      const hint = hints.find((h) => !!h.phoneNumber);

      if (hint?.phoneNumber) {
        setError("");
        setResolver(resv);

        const phoneInfoOptions = {
          multiFactorHint: hint,
          session: resv.session,
        };

        const provider = new PhoneAuthProvider(auth);

        const vId = await provider.verifyPhoneNumber(
          phoneInfoOptions,
          window.recaptchaVerifierLogin
        );

        setLoginVerificationId(vId);
        return;
      }

      setError("");
      setResolver(resv);
    } catch (sendErr) {
      console.error("Failed to send MFA SMS:", sendErr);
      setResolver(null);
      setLoginVerificationId(null);
      setError("Could not send verification code. Please try again.");
    }
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        const userStatusRef = ref(rtdb, `/status/${user.uid}`);

        set(userStatusRef, {
          state: "online",
          last_changed: serverTimestamp(),
        });

        onDisconnect(userStatusRef).set({
          state: "offline",
          last_changed: serverTimestamp(),
        });
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (auth.currentUser) {
        const statusRef = ref(rtdb, `/status/${auth.currentUser.uid}`);

        set(statusRef, {
          state: "offline",
          last_changed: serverTimestamp(),
        });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const fetchUserRole = async (userId) => {
    try {
      const docRef = doc(db, "users", userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data().role;
      }

      return null;
    } catch (error) {
      console.error("Error fetching user role:", error);
      return null;
    }
  };

  const goToJobBank = () => {
    navigate("/jobbank");
  };

  const focusMfaInput = () => {
    requestAnimationFrame(() => {
      mfaInputRef.current?.focus();
    });
  };

  const handleVerifySms = async (event) => {
    event.preventDefault();
    if (verifyLock.current) return;

    if (!resolver || !loginVerificationId || !smsCode) {
      setError("Enter the code sent to your phone.");
      focusMfaInput();
      return;
    }

    verifyLock.current = true;
    setVerifyingMfa(true);
    setError("");

    try {
      const cred = PhoneAuthProvider.credential(loginVerificationId, smsCode);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      const userCredential = await resolver.resolveSignIn(assertion, {
        rememberDevice,
      });

      const user = userCredential.user;
      const userRole = await fetchUserRole(user.uid);

      setUserRole(userRole);
      sessionStorage.setItem("userRole", userRole);

      await ensurePlanState(user.uid);

      if (planIntent) {
        await checkoutForPlanIntent(user, planIntent);
        return;
      }

      if (purchaseIntent > 0) {
        navigate("/purchase");
        return;
      }

      setPurchaseIntent(0);
      goToJobBank();
    } catch (err) {
      console.error("Error verifying SMS code:", err);
      setSmsCode("");
      setError(
        err?.code === "auth/invalid-verification-code"
          ? "That code wasn't accepted. Check the code and try again."
          : "Invalid code. Please try again."
      );
      focusMfaInput();
    } finally {
      verifyLock.current = false;
      setVerifyingMfa(false);
    }
  };

  const handleVerifyTotp = async (event) => {
    event.preventDefault();
    if (verifyLock.current) return;

    if (!resolver) {
      setError("Multi-factor resolver missing. Try signing in again.");
      return;
    }

    const code = String(totpCodeLogin || "")
      .replace(/\s|-/g, "")
      .trim();

    if (!/^\d{6}$/.test(code)) {
      setError(
        mfaFactor === "totp"
          ? "Enter all 6 digits from your authenticator app."
          : "Enter your second-factor code."
      );
      focusMfaInput();
      return;
    }

    verifyLock.current = true;
    setVerifyingMfa(true);
    setError("");

    try {
      const totpHint = resolver.hints.find((h) => h.factorId === "totp");

      if (!totpHint) {
        setError("No authenticator app factor found for this account.");
        return;
      }

      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        code
      );

      const userCredential = await resolver.resolveSignIn(assertion, {
        rememberDevice,
      });

      const user = userCredential.user;
      const userRole = await fetchUserRole(user.uid);

      setUserRole(userRole);
      sessionStorage.setItem("userRole", userRole);

      await ensurePlanState(user.uid);

      if (planIntent) {
        await checkoutForPlanIntent(user, planIntent);
        return;
      }

      if (purchaseIntent > 0) {
        navigate("/purchase");
        return;
      }

      setPurchaseIntent(0);
      goToJobBank();
    } catch (err) {
      console.error("Error verifying TOTP code:", err);
      setTotpCodeLogin("");
      setError(
        err?.code === "auth/invalid-verification-code"
          ? "That code wasn't accepted. Check the code and try again."
          : "Could not verify the authenticator code. Try again."
      );
      focusMfaInput();
    } finally {
      verifyLock.current = false;
      setVerifyingMfa(false);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (loginLock.current || googleLock.current) return;
    loginLock.current = true;
    setLoginPending(true);
    setError("");
    setInfo("");
    setUnverifiedUser(null);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      const user = userCredential.user;

      if (!user.emailVerified) {
        const actionCodeSettings = {
          url: "https://ispeakwell.ca/jobbank-login",
          handleCodeInApp: false,
        };

        await sendEmailVerification(user, actionCodeSettings);

        setUnverifiedUser(user);
        setInfo(
          "Email not verified. A verification email has been sent. Please verify before continuing."
        );

        return;
      }

      const userRole = await fetchUserRole(user.uid);

      setUserRole(userRole);
      sessionStorage.setItem("userRole", userRole);

      await ensurePlanState(user.uid);

      if (planIntent) {
        await checkoutForPlanIntent(user, planIntent);
        return;
      }

      if (purchaseIntent > 0) {
        navigate("/purchase");
        return;
      }

      setPurchaseIntent(0);
      goToJobBank();
    } catch (err) {
      if (err.code === "auth/multi-factor-auth-required") {
        const resv = getMultiFactorResolver(auth, err);
        await startMfaFlow(resv);
      } else {
        console.error("Error logging in:", err);
        setError("An error occurred while trying to log in. Please try again.");
      }
    } finally {
      loginLock.current = false;
      setLoginPending(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      const target = unverifiedUser || auth.currentUser;

      if (!target) {
        setError("Please log in again to resend verification.");
        return;
      }

      const actionCodeSettings = {
        url: "https://ispeakwell.ca/jobbank-login",
        handleCodeInApp: false,
      };

      await sendEmailVerification(target, actionCodeSettings);

      setInfo("Verification email resent. Please check your inbox.");
    } catch (err) {
      console.error("Failed to resend verification email:", err);
      setError("Could not resend verification email.");
    }
  };

  const handleGoogleSignIn = async () => {
    if (googleLock.current || loginLock.current) return;
    googleLock.current = true;
    setGooglePending(true);
    setError("");
    setInfo("");
    setUnverifiedUser(null);
    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();

        if (!userData.firstName || !userData.lastName) {
          let [firstName, lastName] = user.displayName
            ? user.displayName.split(" ")
            : ["", ""];

          lastName = lastName || "";

          await setDoc(
            userDocRef,
            {
              ...userData,
              firstName,
              lastName,
            },
            { merge: true }
          );
        }
      } else {
        let [firstName, lastName] = user.displayName
          ? user.displayName.split(" ")
          : ["", ""];

        lastName = lastName || "";

        await setDoc(userDocRef, {
          email: user.email,
          firstName,
          lastName,
          role: "user",
          tokens: 2,
          desiredPurchase: 0,
          paying: false,
        });
      }

      const userRole = await fetchUserRole(user.uid);

      setUserRole(userRole || "user");
      sessionStorage.setItem("userRole", userRole || "user");

      await ensurePlanState(user.uid);

      if (planIntent) {
        await checkoutForPlanIntent(user, planIntent);
        return;
      }

      if (purchaseIntent > 0) {
        navigate("/purchase");
        return;
      }

      setPurchaseIntent(0);
      goToJobBank();
    } catch (err) {
      if (err.code === "auth/multi-factor-auth-required") {
        const resv = getMultiFactorResolver(auth, err);
        await startMfaFlow(resv);
      } else if (
        err.code === "auth/popup-closed-by-user" ||
        err.code === "auth/cancelled-popup-request"
      ) {
        setError("");
      } else {
        console.error("Error logging in with Google:", err);
        setError("There was an issue with Google sign-in. Please try again.");
      }
    } finally {
      googleLock.current = false;
      setGooglePending(false);
    }
  };

  const navigateTo = (path) => {
    navigate(path);
  };

  return (
    <div className="JBL-container">
      <div className="JBL-card">
        <h1 className="JBL-title">Community Job Bank</h1>

        <p className="JBL-subtitle">Powered by The iSpeakWell Project</p>

        <p className="JBL-description">
          Connecting Local Talent with Local Opportunity.
        </p>

        {showLoginChoices && (
          <>
            <form className="JBL-form" onSubmit={handleLogin}>
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loginPending}
                autoComplete="email"
              />

              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loginPending}
                autoComplete="current-password"
                aria-describedby={error ? "jbl-login-error" : undefined}
                aria-invalid={error ? "true" : undefined}
              />

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "0.9rem",
                  color: "rgba(255,255,255,0.85)",
                  marginTop: "4px",
                }}
              >
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  style={{ marginRight: "8px" }}
                  disabled={loginPending}
                />
                Remember this device
              </label>

              {rememberDevice && (
                <p
                  style={{
                    fontSize: "0.85rem",
                    color: "rgba(255,255,255,0.75)",
                    marginTop: "0",
                  }}
                >
                  We won’t ask for a code again on this device for 30 days.
                </p>
              )}

              <button type="submit" className="JBL-login-btn" disabled={loginPending}>
                {loginPending ? "Signing in…" : "Sign In"}
              </button>
            </form>

            {error && (
              <p id="jbl-login-error" className="JBL-error" role="alert">{error}</p>
            )}

            {info && <p className="JBL-info">{info}</p>}

            {unverifiedUser && (
              <button
                type="button"
                className="JBL-login-btn"
                style={{ width: "100%", marginTop: "18px" }}
                onClick={handleResendVerification}
              >
                Resend Verification Email
              </button>
            )}

            <button
              type="button"
              className="JBL-google-btn"
              onClick={handleGoogleSignIn}
              disabled={loginPending}
            >
              <img src={googleLogo} alt="Google" />
              Continue with Google
            </button>

            <div className="JBL-links">
              <Link to="/jobbank/signup">Create Account</Link>
              <Link to="/jobbank/password-reset">Forgot Password?</Link>
            </div>
          </>
        )}

        {googlePending && !mfaFactor && (
          <p className="JBL-helper" role="status">Signing in with Google…</p>
        )}

        {mfaFactor === "sms" && !loginVerificationId && (
          <p className="JBL-helper" role="status">Sending a verification code to your phone…</p>
        )}

        {mfaFieldReady && (
          <form
            className="JBL-form"
            onSubmit={mfaFactor === "sms" ? handleVerifySms : handleVerifyTotp}
          >
            <h2 id="jbl-mfa-title" className="JBL-mfa-title">
              {mfaFactor === "sms"
                ? "Enter your text message code"
                : mfaFactor === "other"
                  ? "Enter your verification code"
                  : "Enter your authenticator code"}
            </h2>
            <p id="jbl-mfa-hint" className="JBL-helper">
              {mfaFactor === "sms"
                ? "Enter the code sent to your phone."
                : mfaFactor === "other"
                  ? "Enter your second-factor code."
                  : "Enter the 6-digit code from your authenticator app."}
            </p>
            <input
              ref={mfaInputRef}
              id="jbl-mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              autoFocus
              aria-labelledby="jbl-mfa-title"
              aria-describedby={error ? "jbl-mfa-hint jbl-mfa-error" : "jbl-mfa-hint"}
              aria-invalid={error ? "true" : undefined}
              value={mfaFactor === "sms" ? smsCode : totpCodeLogin}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, "").slice(0, 6);
                if (mfaFactor === "sms") setSmsCode(next);
                else setTotpCodeLogin(next);
                if (error) setError("");
              }}
              disabled={verifyingMfa}
            />
            {error && (
              <p id="jbl-mfa-error" className="JBL-error" role="alert">{error}</p>
            )}
            <button type="submit" className="JBL-login-btn" disabled={verifyingMfa}>
              Verify Code
            </button>
          </form>
        )}

        <div id="recaptcha-login-container" style={{ display: "none" }} />
      </div>
    </div>
  );
}

export default JobBankLogin;